<template>
  <BittsModal
    :visible="true"
    :loading="!ready"
    :action-loading="loading"
    :width="640"
    :use-mask-to-close="true"
    :disabled="!selectedCompany || cannotSendInvite"
    :primary-button-text="saveText"
    @closed="handleCloseModal"
    @action="handleAddPartner"
    title="Invite a partner to connect on Crossbeam"
    description="Crossbeam is free for them to use."
    class="c-onboarding-invite-partner-modal"
  >
    <template #image>
      <BittsAvatarStack
        :org-list="[
          { ...currentOrg, showInitials: true },
          {
            customSvg: 'add',
          },
        ]"
        :show-initials="true"
        :transparent="false"
        size="medium"
        class="mb-16"
      />
    </template>
    <InvitePartnerSearch class="w-full mb-16" @input="companyChanged" />
    <div v-if="!proposalWarningTitle">
      <BittsSelect
        v-if="potentialContacts.length > 0"
        v-model="chosenContactId"
        :options="potentialContacts"
        :allow-clear="true"
        :disabled="!selectedCompany"
        data-testid="discoverable-contact-selector"
        placeholder="Select contact"
        :form-label="{
          title: 'Contact',
          secondaryText: isPartnerDiscoverable ? 'Optional' : '',
        }"
        option-type="entity"
        class="mb-16"
      />
      <BittsInput
        v-else
        v-model="nonDiscoveredContactEmail"
        :disabled="!selectedCompany"
        :status="!!nonDiscoveredContactEmailErr ? 'danger' : 'default'"
        :danger-text="nonDiscoveredContactEmailErr"
        name="contact-email-input"
        data-testid="contact-email-input"
        placeholder="Enter a contact email"
        :form-label="{
          title: 'Contact',
          secondaryText: isPartnerDiscoverable ? 'Optional' : '',
        }"
        class="mb-16"
      />
      <BittsTextArea
        v-model="inviteMessage"
        data-testid="proposal-message-input"
        :disabled="!selectedCompany"
        :status="inviteMessageErr ? 'danger' : 'default'"
        :danger-text="inviteMessageErr"
        :form-label="{
          title: 'Write your message',
          secondaryText: 'Optional',
        }"
        placeholder="Enter a message"
        class="mb-16"
      />
      <ActivationTeamCcCallout
        v-if="canShowActivationTeamCc"
        v-model="activationTeamCc"
        class="mb-16"
      />
      <!-- <BittsCheckbox
        :checked="sendCopy"
        @input="sendCopy = !sendCopy"
        label="Send me a copy of the invite"
        class="mb-16"
      /> -->
    </div>
    <BittsAlert
      v-if="proposalWarningTitle"
      data-testid="proposal-warning"
      :color="proposalWarningState"
      :message="proposalWarningTitle"
      :description="proposalWarningMsg"
      class="mb-16"
    />
    <BittsAlert
      v-if="!proposalWarningTitle"
      message="No data is shared. You'll decide what to share after they accept your invitation."
      color="success"
    />
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsAvatarStack,
  BittsInput,
  BittsSelect,
  BittsTextArea,
} from '@crossbeam/bitts';
import BittsModal from '@crossbeam/bitts/src/BittsModal/BittsModal.vue';

import { email as emailVal } from '@vuelidate/validators';
import { useLocalStorage } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ActivationTeamCcCallout from '@/components/partners/AddPartner/ActivationTeamCcCallout.vue';
import InvitePartnerSearch, {
  Company,
} from '@/components/partners/AddPartner/InvitePartnerSearch.vue';

import useAuth from '@/composables/useAuth';
import useInvitePartnerForm from '@/composables/useInvitePartnerForm';
import usePartnerManagement from '@/composables/usePartnerManagement';
import useSmartOnboarding from '@/composables/useSmartOnboarding';
import {
  allReady,
  useBillingStore,
  useDataSharesStore,
  usePartnersStore,
} from '@/stores';

const { currentOrg } = useAuth();
const router = useRouter();
const route = useRoute();
const dataSharesStore = useDataSharesStore();
const partnersStore = usePartnersStore();
const billingStore = useBillingStore();
const { isSmartOnboarding } = useSmartOnboarding();
const { sendInvitation } = usePartnerManagement();

const { proposalsSent, proposalsReceived } = storeToRefs(partnersStore);
const { isNotPaying } = storeToRefs(billingStore);
const ready = allReady(partnersStore, dataSharesStore);
onMounted(() => {
  const { name, domain, public_invite_code: pic } = route.query;
  if (name && domain && pic)
    selectedCompany.value = {
      name,
      domain,
      public_invite_code: pic,
      clearbit_domain: domain,
    } as Company;
});

const loading = ref<boolean>(false);

const {
  selectedCompany,
  chosenContactId,
  nonDiscoveredContactEmail,
  inviteMessage,
  activationTeamCc,
  canShowActivationTeamCc,
  potentialContacts,
  isPartnerDiscoverable,
  existingPartner,
  selectedContact,
  handleCompanyChanged,
  buildContactPayload,
} = useInvitePartnerForm();

// TODO: Bring back the checkbox for sending a copy of the invite when we have the property for payload
// const sendCopy = ref(true);

// Errors
const nonDiscoveredContactEmailErr = computed(() => {
  if (!nonDiscoveredContactEmail.value) return '';
  return !emailVal.$validator(
    nonDiscoveredContactEmail.value.toLowerCase(),
    null,
    null,
  )
    ? 'Please enter a valid email'
    : '';
});
const inviteMessageErr = computed(() =>
  inviteMessage.value.length > 500
    ? 'Invite messages may only be 500 characters'
    : '',
);

const saveText = computed(() => {
  return proposalAlreadyExists.value ? 'View invitation' : 'Invite';
});

function handleCloseModal() {
  if (isSmartOnboarding.value) {
    router.back();
    return;
  }
  const bookedOrDismissedMeetingModal = useLocalStorage(
    'bookedOrDismissedMeetingModal',
    false,
  );
  if (!bookedOrDismissedMeetingModal.value && isNotPaying.value) {
    router.push({ name: 'onboarding_book_meeting' });
  } else {
    router.back();
  }
}

async function companyChanged({ company }: { company: Company }) {
  await handleCompanyChanged({ company });

  /* Re-select dropdown after clearing, otherwise open/close based on
  whether we need additional information or not from the user */
  const input = document.querySelector<HTMLInputElement>(
    '#invite-partner-search input',
  );
  if (!company?.name) input?.focus();
  else if (!isPartnerDiscoverable.value) input?.blur();
}

/* Proposal Warning */
const clearbitDomainsFromReceivedProposals = computed(() =>
  proposalsReceived.value.map(
    (proposal) => proposal?.sending_organization?.clearbit_domain,
  ),
);

const proposalAlreadyExists = computed(() =>
  clearbitDomainsFromReceivedProposals.value.some(
    (val) => val === selectedCompany.value?.domain,
  ),
);

const currentOrgSentProposal = computed(() =>
  proposalsSent.value.some(
    (proposal) =>
      proposal.contact_company === selectedCompany.value?.name &&
      proposal.status === 'pending',
  ),
);

const proposalWarningTitle = computed(() => {
  if (existingPartner.value)
    return `You're already partnered with ${existingPartner.value.name}`;
  if (proposalAlreadyExists.value)
    return `You have a partnership request from ${selectedCompany.value?.name}`;
  if (currentOrgSentProposal.value)
    return `You have an open partnership request to ${selectedCompany.value?.name}`;
  return '';
});
const proposalWarningMsg = computed(() => {
  if (existingPartner.value) return 'Remove from search to continue';
  if (proposalAlreadyExists.value)
    return 'Accept their invitation to collaborate live on Crossbeam';
  if (currentOrgSentProposal.value)
    return 'If you want to send a new one, revoke the existing invitation';
  return '';
});

const proposalWarningState = computed(() => {
  return proposalAlreadyExists.value ? 'success' : 'warning';
});

const cannotSendInvite = computed(() => {
  if (proposalAlreadyExists.value) return false;
  if (proposalWarningTitle.value) return true;

  if (isPartnerDiscoverable.value) return !selectedCompany.value;

  return (
    !!nonDiscoveredContactEmailErr.value ||
    !selectedCompany.value ||
    (!selectedContact.value && !nonDiscoveredContactEmail.value)
  );
});

async function handleAddPartner() {
  loading.value = true;
  if (proposalAlreadyExists.value) {
    const existingIncomingProposal = proposalsReceived.value.find(
      (proposal) =>
        proposal?.sending_organization?.clearbit_domain ===
        selectedCompany?.value?.domain,
    );
    await router.push({
      name: 'accept_proposal',
      query: { proposal_id: existingIncomingProposal?.id },
    });
    return;
  }

  await sendInvitation({
    company: selectedCompany.value,
    contact: buildContactPayload(),
    inviteMessage: inviteMessage.value,
    activationTeamCc: activationTeamCc.value,
  });
  router.back();
}
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.c-onboarding-invite-partner-modal {
  .o-bitts-modal__header {
    @apply p-40!;
  }
  .bitts-avatar-stack-medium {
    .bitts-avatar-stack__org-list {
      @apply justify-center p-0 mb-10;
      & > div {
        @apply w-[52px];
      }
      img {
        @apply bg-denim-200;
      }
    }
  }
  .o-bitts-modal__description {
    @apply text-neutral-text-weakest! text-base!;
  }
  .bitts-avatar-stack__transparent .bitts-avatar-stack__org-list {
    background-color: transparent;
  }

  .bitts-form-label__text {
    @apply text-neutral-text-strong!;
  }

  textarea.c-bitts-textarea.ant-input {
    &.disabled {
      @apply bg-neutral-50 outline-hidden border-neutral-100;
    }
  }
  textarea.c-bitts-textarea.ant-input::placeholder {
    &.disabled {
      @apply text-neutral-300;
    }
  }
}
</style>
